import React from "react";
import type { TForm } from "./EosForm";

import radioEmpty from "../../../assets/radio-button-on.svg";
import radioFilled from "../../../assets/radio-button-on-fill.svg";

type AntibioticTreatment = NonNullable<TForm["antibioticTreatment"]>;

type Props = {
  value: TForm["antibioticTreatment"];
  onChange: (value: AntibioticTreatment) => void;
};

const OPTIONS: Array<{ key: AntibioticTreatment; label: string }> = [
  { key: "none", label: "ללא או טיפול פחות משעתיים לפני הלידה" },
  { key: "GBS-2", label: "ספציפית ל-GBS מעל שעתיים לפני הלידה" },
  { key: "broad-2", label: "רחבת טווח 2 - 3.9 שעות לפני הלידה" },
  { key: "broad-4", label: "רחבת טווח מעל 4 שעות לפני הלידה" },
];

export const AntibioticTreatmentSelect: React.FC<Props> = ({
  value,
  onChange,
}) => {
  //  keep a stable index even when value is undefined
  const selectedIndex = Math.max(0, OPTIONS.findIndex((o) => o.key === value));

  const selectByIndex = (idx: number) => {
    const clamped = Math.max(0, Math.min(OPTIONS.length - 1, idx));
    onChange(OPTIONS[clamped].key);
  };

  //  keyboard navigation similar to native radio group
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      selectByIndex(selectedIndex + 1);
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      selectByIndex(selectedIndex - 1);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      selectByIndex(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      selectByIndex(OPTIONS.length - 1);
      return;
    }
    if (e.key === " " || e.key === "Enter") {
      //  re-select current option (useful when value is undefined)
      e.preventDefault();
      selectByIndex(selectedIndex);
    }
  };

  return (
    <div className="form-grid" data-testid="antibiotics">
      <div className="grid-input" style={{ width: "100%" }}>
        <div
          style={{
            maxWidth: "560px",
            marginInlineStart: "auto",
            direction: "rtl",
          }}
        >
          <div
            role="radiogroup"
            aria-label="antibioticTreatment"
            data-testid="antibiotics-group"
            tabIndex={0}
            onKeyDown={onKeyDown}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "22px",
              outline: "none",
            }}
          >
            {OPTIONS.map((opt) => {
              const checked = value === opt.key;

              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  //  stable per-option test id used by Playwright
                  data-testid={`antibiotics-option-${opt.key}`}
                  onClick={() => onChange(opt.key)}
                  onKeyDown={(e) => {
                    //  allow per-option activation as well
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      onChange(opt.key);
                    }
                  }}
                  //  render as a row (no default button styling)
                  style={{
                    display: "flex",
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    cursor: "pointer",
                    userSelect: "none",
                    textAlign: "inherit",
                  }}
                >
                  {/* Icon */}
                  <span style={{ flex: "0 0 auto" }}>
                    <img
                      src={checked ? radioFilled : radioEmpty}
                      alt=""
                      data-testid={`antibiotics-icon-${opt.key}`}
                      style={{
                        width: "18px",
                        height: "18px",
                        display: "block",
                      }}
                    />
                  </span>

                  {/* Text */}
                  <span
                    data-testid={`antibiotics-label-${opt.key}`}
                    style={{
                      flex: 1,
                      color: "#0C3058",
                      fontSize: "1rem",
                      lineHeight: 1.2,
                      textAlign: "right",
                    }}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};